/**
 * The server-rendered sheet, fetched as a document this page can hold.
 *
 * What comes back is the SAME artifact the download hands over — rendered once,
 * by the API, from the resolved occasion. Nothing here draws a sheet; showing
 * one and printing one are two things to do with the one document, which is
 * what keeps them from disagreeing about what is on the page.
 *
 * It is FETCHED rather than pointed at, because the routes answer a refusal
 * with a page: no session, no permission, or no renderer, and each of those
 * comes back as HTML or JSON that a frame loads perfectly happily. Pointed at,
 * the print dialog opens over the words "You need to be signed in" and somebody
 * takes that to the wall. Read first, the refusal is a refusal.
 */
export class SheetUnavailableError extends Error {
  constructor(readonly status: number) {
    super(`The sheet came back as ${status}`)
    this.name = 'SheetUnavailableError'
  }
}

/**
 * Hand back an object URL for the sheet at `url`, or throw.
 *
 * The caller owns what comes back and revokes it when done — the blob is the
 * whole document, so one left behind holds it for the life of the tab.
 */
export async function fetchSheet(url: string): Promise<string> {
  // Same-origin, so the session rides along exactly as it does for a download.
  const res = await fetch(url, { credentials: 'same-origin' })
  const type = res.headers.get('content-type') ?? ''
  if (!res.ok || !type.startsWith('application/pdf'))
    throw new SheetUnavailableError(res.status)
  return URL.createObjectURL(await res.blob())
}

/**
 * Send the sheet straight to the printer.
 *
 * The document is held in an offscreen frame and printed from there, so the
 * dialog opens over the app: nothing lands in the downloads folder and no
 * viewer tab is left to close.
 *
 * Where the sheet cannot be had — or where the browser will not print one it is
 * holding in a frame — it is OPENED instead, which is what the download beside
 * this does. That is the whole of the fallback, and it is worth saying what it
 * does not cover: a browser that neither throws nor prints leaves this silent,
 * and the download is then the way to the printer.
 */
export async function printPdf(url: string): Promise<void> {
  let href: string
  try {
    href = await fetchSheet(url)
  } catch {
    // The server's own page says what went wrong, so open it rather than
    // inventing a second account of it.
    window.open(url, '_blank', 'noopener')
    return
  }

  const frame = document.createElement('iframe')
  /* Offscreen rather than hidden: a frame with `display: none` is not laid out,
     and a viewer that was never laid out has nothing to print. */
  frame.style.cssText =
    'position:fixed;right:0;bottom:0;width:1px;height:1px;opacity:0;border:0'
  frame.src = href

  /* Both the frame and the blob it holds go together, and only once the dialog
     is finished with them: printing is modal and raises no event of its own, so
     the app being handed focus back is the signal, and the timeout is what
     answers a browser that never hands it back. */
  let dropped = false
  const drop = () => {
    if (dropped) return
    dropped = true
    window.removeEventListener('focus', drop)
    setTimeout(() => {
      frame.remove()
      URL.revokeObjectURL(href)
    }, 1000)
  }

  frame.onload = () => {
    const view = frame.contentWindow
    if (!view) {
      frame.remove()
      URL.revokeObjectURL(href)
      window.open(url, '_blank', 'noopener')
      return
    }
    try {
      view.focus()
      view.print()
      window.addEventListener('focus', drop, { once: true })
      setTimeout(drop, 60_000)
    } catch {
      frame.remove()
      URL.revokeObjectURL(href)
      window.open(url, '_blank', 'noopener')
    }
  }

  document.body.append(frame)
}
